import { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, formatDistanceToNow, isPast } from "date-fns";
import { useParams } from "react-router-dom";
import {
  mapChannelStatusToDisplay,
  getStatusBadgeClasses,
} from "../../../utils/journeyStatusUtils";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import Modal from "../../modal";

import Autocomplete from "../../autocomplete";

import {
  Journey,
  PublishedJourneyChannel,
  JourneyChannelScheduleStatus,
} from "../../../types/journey";
import { useApi } from "../../../hooks/useApi";
import { useTeamContext } from "../../../contexts/TeamContext";
import {
  useJourneyDetail,
  usePublishJourney,
  journeyKeys,
} from "../../../hooks/useJourneys";

import {
  faCalendarAlt,
  faUsers,
  faInfoCircle,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

interface ChannelSchedule {
  id: string;
  channel_id: string;
  name: string;
  start_date: string;
  status: JourneyChannelScheduleStatus;
}

interface Channel {
  id: string;
  name: string;
  is_private: boolean;
  channel_id: string;
}

export const JourneyAudienceContent = () => {
  const { teamId } = useTeamContext();
  const { journeyId } = useParams<{ journeyId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();
  const { data: journeyData } = useJourneyDetail(teamId, journeyId || "");
  const publishJourneyMutation = usePublishJourney(teamId, journeyId || "");

  // State for cancel confirmation modal
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancellingChannel, setCancellingChannel] =
    useState<PublishedJourneyChannel | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // Available channels from Slack
  const [availableChannels, setAvailableChannels] = useState<Channel[]>([]);
  // Selected channels for scheduling
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);

  // Channel schedules for the journey, set during publishing local state
  const [channelSchedules, setChannelSchedules] = useState<
    Record<string, ChannelSchedule>
  >({});

  // Track which channels are currently being published
  const [publishingChannels, setPublishingChannels] = useState<Set<string>>(
    new Set(),
  );

  // Current journey data
  const [journey, setJourney] = useState<Journey>();

  // Default to tomorrow for new channel schedules (date only)
  const defaultStartDate = format(
    new Date(Date.now() + 24 * 60 * 60 * 1000),
    "yyyy-MM-dd",
  );

  // Today's date for min attribute in date picker (to prevent selecting past dates)
  const todayDate = format(new Date(), "yyyy-MM-dd");
  const [loading, setLoading] = useState<boolean>(false);

  // Set journey data when it changes
  useEffect(() => {
    if (journeyData) {
      setJourney(journeyData);
    }
  }, [journeyData]);

  // Toggle channel selection
  const toggleChannelSelection = useCallback(
    (channelId: string) => {
      setSelectedChannels((prev) => {
        // If we're adding the channel (not removing it)
        if (!prev.includes(channelId)) {
          // Initialize the channel schedule with the default date
          const channel = availableChannels.find((c) => c.id === channelId);
          if (channel) {
            setChannelSchedules((schedules) => ({
              ...schedules,
              [channelId]: {
                id: channelId,
                channel_id: channel.channel_id,
                name: channel.name,
                start_date: defaultStartDate,
                status: "draft" as JourneyChannelScheduleStatus,
              },
            }));
          }
          return [...prev, channelId];
        } else {
          // Removing the channel
          // Also remove from channelSchedules
          setChannelSchedules((prev) => {
            const updated = { ...prev };
            delete updated[channelId];
            return updated;
          });
          return prev.filter((id) => id !== channelId);
        }
      });
    },
    [availableChannels, defaultStartDate],
  );

  // Update channel schedule
  const updateChannelSchedule = useCallback(
    (channelId: string, startDate: string) => {
      const channel = availableChannels.find((c) => c.id === channelId);
      if (!channel) return;

      // Don't allow past dates
      const selectedDate = new Date(startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time part for date comparison

      if (selectedDate < today) {
        toast.error("Cannot schedule a journey to start in the past");
        return;
      }

      setChannelSchedules((prev) => ({
        ...prev,
        [channelId]: {
          id: channelId,
          channel_id: channel.channel_id,
          name: channel.name,
          start_date: startDate,
          status: "draft" as JourneyChannelScheduleStatus,
        },
      }));
    },
    [availableChannels],
  );

  // Track if we're currently refreshing journey data
  const [isRefreshingJourney, setIsRefreshingJourney] =
    useState<boolean>(false);

  // Check if a channel is ready for publishing
  const isChannelReadyForPublish = useCallback(
    (channelId: string): { isReady: boolean; reason?: string } => {
      // Special case: If it's a newly selected channel, use the default date
      if (
        !channelSchedules[channelId] &&
        selectedChannels.includes(channelId)
      ) {
        // If channel is selected but no schedule yet, it should be publishable with default date
        const channel = availableChannels.find((c) => c.id === channelId);
        if (channel) {
          return { isReady: true };
        }
      }

      // 1. Check if we have a valid channel
      const channel = availableChannels.find((c) => c.id === channelId);
      if (!channel) {
        return { isReady: false, reason: "Invalid channel" };
      }

      // 2. Check if we have a start date
      const schedule = channelSchedules[channelId];
      if (!schedule?.start_date) {
        return { isReady: false, reason: "Start date is required" };
      }

      // 3. Check if start date is not in the past
      const selectedDate = new Date(schedule.start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time part for date comparison

      if (selectedDate < today) {
        return { isReady: false, reason: "Cannot schedule in the past" };
      }

      // 4. Check if the channel is already being published
      if (publishingChannels.has(channelId)) {
        return { isReady: false, reason: "Publishing in progress" };
      }

      // All checks passed
      return { isReady: true };
    },
    [availableChannels, channelSchedules, publishingChannels, selectedChannels],
  );

  // Force refresh of journey data
  const refreshJourneyData = useCallback(async () => {
    if (!teamId || !journeyId || isRefreshingJourney) return;

    setIsRefreshingJourney(true);
    try {
      await queryClient.invalidateQueries({
        queryKey: journeyKeys.detail(teamId, journeyId),
      });
      await queryClient.invalidateQueries({
        queryKey: journeyKeys.list(teamId),
      });
    } finally {
      setIsRefreshingJourney(false);
    }
  }, [teamId, journeyId, queryClient, isRefreshingJourney]);

  // Publish journey to a specific channel
  const publishChannel = useCallback(
    async (channelId: string) => {
      // Handle newly selected channels that don't have a schedule yet
      let schedule = channelSchedules[channelId];
      if (!schedule && selectedChannels.includes(channelId)) {
        const channel = availableChannels.find((c) => c.id === channelId);
        if (channel) {
          // Create schedule with default date
          schedule = {
            id: channelId,
            channel_id: channel.channel_id,
            name: channel.name,
            start_date: defaultStartDate,
            status: "draft" as JourneyChannelScheduleStatus,
          };
        }
      }

      if (!schedule) return;

      // Set this channel as currently publishing
      setPublishingChannels((prev) => new Set([...prev, channelId]));

      try {
        // Send the API request with channel_id and start_date
        await publishJourneyMutation.mutateAsync({
          channel_id: schedule.channel_id,
          start_date: schedule.start_date,
        });
        toast.success(`Journey scheduled for #${schedule.name}`);

        // Clear this channel from the selection since it's now published
        setSelectedChannels((prev) => prev.filter((id) => id !== channelId));
        setChannelSchedules((prev) => {
          const updated = { ...prev };
          delete updated[channelId];
          return updated;
        });

        // Force a refresh to get the latest journey data
        await refreshJourneyData();
      } catch (error) {
        console.error("Error publishing journey:", error);
        if (error instanceof Error) {
          toast.error(`Failed to publish journey: ${error.message}`);
        } else {
          toast.error("Failed to publish journey");
        }
      } finally {
        // Remove this channel from the publishing state regardless of outcome
        setPublishingChannels((prev) => {
          const updated = new Set([...prev]);
          updated.delete(channelId);
          return updated;
        });
      }
    },
    [
      channelSchedules,
      publishJourneyMutation,
      refreshJourneyData,
      selectedChannels,
      availableChannels,
      defaultStartDate,
    ],
  );

  // Fetch available Slack channels for team
  const fetchChannels = useCallback(async () => {
    if (!teamId) return;

    try {
      setLoading(true);
      const response = await api.get(`/v2/slack/channels?team=${teamId}`);
      setAvailableChannels(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching channels:", error);
      toast.error("Failed to load channels");
      setLoading(false);
    }
  }, [api, teamId]);

  useEffect(() => {
    // Fetch real channels from API
    fetchChannels();
  }, [fetchChannels]);

  return (
    <div className="space-y-8 p-4">
      {/* Journey Status Card */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-brand" />
            Journey Schedules
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            View your published journeys.
          </p>
        </div>

        {/* Show published journeys in a table format */}
        <div className="overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Channel
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Started
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {journey?.channels.map(
                (channel: PublishedJourneyChannel, index: number) => {
                  // Only show channels that are published or further in the lifecycle
                  const isActive = [
                    "published",
                    "in_progress",
                    "completed",
                  ].includes(channel.status);

                  if (!isActive) return null;

                  return (
                    <tr key={index}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-md bg-brand/10 flex items-center justify-center mr-3">
                            <span className="text-brand font-medium">#</span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {channel.name}
                            </div>
                            <div className="text-gray-500 text-xs">
                              {channel.channel_private
                                ? "Private channel"
                                : "Public channel"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {(() => {
                          // Get mapped display status and style classes
                          const displayStatus = mapChannelStatusToDisplay(
                            channel.status,
                          );
                          const statusClasses =
                            getStatusBadgeClasses(displayStatus);

                          return (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses.bg} ${statusClasses.text}`}
                            >
                              {displayStatus}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {channel.start_date ? (
                          <>
                            {/* Find first scheduled game for this channel to get proper datetime */}
                            {(() => {
                              // Find scheduled games for this channel by checking journey steps
                              const scheduledSteps =
                                journeyData?.steps?.filter(
                                  (step) => step.scheduled_game,
                                ) || [];

                              if (scheduledSteps.length > 0) {
                                // Get the first scheduled game
                                const firstScheduled =
                                  scheduledSteps[0].scheduled_game;

                                if (firstScheduled) {
                                  // The API now provides dates with the correct timezone offset
                                  // e.g., "2025-04-17T11:00:00-04:00"
                                  // We can simply parse it with the standard Date constructor
                                  const scheduledDate = new Date(
                                    firstScheduled.scheduled_date,
                                  );

                                  return (
                                    <>
                                      <div>
                                        {format(scheduledDate, "MMM d, yyyy")}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        {format(scheduledDate, "h:mm a")}·{" "}
                                        {firstScheduled.user_timezone}
                                      </div>

                                      {/* Show "starts in" for future published journeys */}
                                      {channel.status === "published" &&
                                        !isPast(scheduledDate) && (
                                          <div className="text-xs font-medium text-brand-dark mt-1">
                                            Starts{" "}
                                            {formatDistanceToNow(
                                              scheduledDate,
                                              { addSuffix: true },
                                            )}
                                          </div>
                                        )}
                                    </>
                                  );
                                }
                              }

                              // Fallback to using channel start date if no scheduled games found
                              const startDate = new Date(channel.start_date);

                              // Use the same date-fns formatting as above for consistency
                              return (
                                <>
                                  <div>{format(startDate, "MMM d, yyyy")}</div>
                                  <div className="text-xs text-gray-400">
                                    {format(startDate, "h:mm a")}
                                  </div>
                                  {channel.status === "published" &&
                                    !isPast(startDate) && (
                                      <div className="text-xs font-medium text-brand-dark mt-1">
                                        Starts{" "}
                                        {formatDistanceToNow(startDate, {
                                          addSuffix: true,
                                        })}
                                      </div>
                                    )}
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <span className="text-gray-400">Not started</span>
                        )}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        {/* Action buttons based on status */}
                        <div className="space-x-3">
                          {/* Cancel button  */}
                          {channel.status !== "completed" && (
                            <button
                              type="button"
                              onClick={() => {
                                setCancellingChannel(channel);
                                setIsCancelModalOpen(true);
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              Cancel
                            </button>
                          )}

                          {/* Archive button - only for completed channels */}
                          {channel.status === "completed" && (
                            <button
                              type="button"
                              disabled={isArchiving}
                              onClick={async () => {
                                try {
                                  setIsArchiving(true);

                                  // Use the same API endpoint as cancellation to remove the channel schedule
                                  await api.post(
                                    `/v2/slack/journeys/${journeyId}/schedules/${channel.id}/archive?team=${teamId}`,
                                  );

                                  // Refresh journey data to update UI
                                  await refreshJourneyData();

                                  toast.success(
                                    `Archived ${channel.name} from journey`,
                                  );
                                } catch (error) {
                                  console.error(
                                    "Error archiving channel:",
                                    error,
                                  );
                                  toast.error("Failed to archive channel");
                                } finally {
                                  setIsArchiving(false);
                                }
                              }}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                },
              )}

              {/* Show empty state if no active channels */}
              {journey?.channels.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="py-8 text-center text-sm text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="h-5 w-5 text-gray-400 mb-2"
                      />
                      <p className="text-xs mt-1">
                        No published journeys yet, publish a journey below.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Publish Journey Section */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-brand" />
            Publish Journey
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Determine when your journey will begin and select the channels where
            it will be assigned.
          </p>
        </div>

        <div className="p-6 space-y-8">
          {/* Channel Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <FontAwesomeIcon icon={faUsers} className="mr-2 text-gray-500" />
              Choose channels
            </h3>

            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">
                  Loading channels...
                </p>
              </div>
            ) : availableChannels.length === 0 ? (
              <div className="text-center bg-gray-50 py-6 rounded-lg border border-gray-200">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="text-gray-400 text-2xl mb-2"
                />
                <p className="text-gray-600 font-medium">
                  No channels available
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Add the Braid Bot to channels in Slack to make them available
                  here.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-md shadow-sm">
                  <Autocomplete
                    items={availableChannels}
                    value=""
                    onChange={(value) => {
                      const channel = availableChannels.find(
                        (channel) => channel.name === value,
                      );
                      if (channel && !selectedChannels.includes(channel.id)) {
                        setSelectedChannels([...selectedChannels, channel.id]);
                      }
                    }}
                    getOptionLabel={(channel) =>
                      `#${channel.name}${channel.is_private ? " (Private)" : ""}`
                    }
                    getOptionValue={(channel) => channel.name}
                    placeholder="Search channels..."
                    className="w-full h-full"
                    emptyMessage="No matching channels found"
                  />
                </div>

                {selectedChannels.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-md shadow-sm max-h-48 overflow-y-auto">
                    <ul className="divide-y divide-gray-200">
                      {selectedChannels.map((channelId) => {
                        const channel = availableChannels.find(
                          (channel) => channel.id === channelId,
                        );
                        if (!channel) return null;

                        return (
                          <li key={channel.id} className="px-4 py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-700">
                                  #{channel.name}
                                </span>
                                {channel.is_private && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                    Private
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  toggleChannelSelection(channel.id)
                                }
                                className="text-gray-400 hover:text-gray-500"
                              >
                                <span className="sr-only">Remove channel</span>
                                <svg
                                  className="h-5 w-5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                <div className="flex items-center justify-end">
                  <span className="text-sm text-gray-500">
                    {selectedChannels.length} channel
                    {selectedChannels.length !== 1 ? "s" : ""} selected
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Channel Schedules */}
          {selectedChannels.length > 0 && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    <FontAwesomeIcon
                      icon={faClock}
                      className="mr-2 text-gray-500"
                    />
                    Channel Schedules
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Set the start time and status for each channel. Times will
                    be in your timezone.
                  </p>
                </div>
              </div>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Channel
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Start Date
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {/* Show selected channels not yet published */}
                    {selectedChannels.map((channelId) => {
                      const channel = availableChannels.find(
                        (c) => c.id === channelId,
                      );
                      if (!channel) return null;

                      const schedule = channelSchedules[channelId];
                      const startDate =
                        schedule?.start_date || defaultStartDate;

                      return (
                        <tr
                          key={channel.id}
                          className={
                            publishingChannels.has(channel.id)
                              ? "opacity-70 bg-gray-50"
                              : ""
                          }
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <div className="h-8 w-8 rounded-md bg-brand/10 flex items-center justify-center mr-3">
                                <span className="text-brand font-medium">
                                  #
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {channel.name}
                                </div>
                                <div className="text-gray-500 text-xs">
                                  {channel.is_private
                                    ? "Private channel"
                                    : "Public channel"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <div className="flex flex-col">
                              <input
                                type="date"
                                value={startDate}
                                min={todayDate}
                                onChange={(e) =>
                                  updateChannelSchedule(
                                    channel.id,
                                    e.target.value,
                                  )
                                }
                                disabled={publishingChannels.has(channel.id)}
                                className="block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                              />
                            </div>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            {(() => {
                              const publishStatus = isChannelReadyForPublish(
                                channel.id,
                              );
                              return (
                                <div className="flex flex-col items-end">
                                  <button
                                    type="button"
                                    onClick={() => publishChannel(channel.id)}
                                    disabled={!publishStatus.isReady}
                                    title={
                                      publishStatus.reason || "Click to publish"
                                    }
                                    className="rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:bg-gray-300 disabled:cursor-not-allowed"
                                  >
                                    {publishingChannels.has(channel.id) ? (
                                      <>
                                        <span className="inline-block mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em]"></span>
                                        Publishing...
                                      </>
                                    ) : (
                                      "Publish"
                                    )}
                                  </button>
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}

                    {selectedChannels.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          className="text-center py-4 text-sm text-gray-500"
                        >
                          No channels selected for scheduling
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Help text */}
              <div className="pt-4 border-t border-gray-200 mt-6">
                <div className="mt-2 text-sm text-gray-500">
                  <p className="flex items-center">
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="mr-2 text-gray-400"
                    />
                    Use the Publish button in each row to publish channels
                    individually
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setCancellingChannel(null);
          setIsCancelling(false);
        }}
      >
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Cancel Journey in Channel
          </h3>
          <div className="mb-6">
            <div className="p-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-md mb-4">
              <p className="font-medium mb-1">
                Warning: This will cancel scheduled games
              </p>
              <p>Cancelling this journey in {cancellingChannel?.name} will:</p>
              <ul className="list-disc pl-5 mt-2 text-sm">
                <li>Mark all upcoming scheduled games as cancelled</li>
                <li>Prevent future games from being sent to the channel</li>
                <li>Not affect games that have already been sent</li>
              </ul>
            </div>
            <p className="text-gray-600">
              Are you sure you want to cancel this journey in the channel? This
              action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setIsCancelModalOpen(false);
                setCancellingChannel(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isCancelling}
            >
              Go Back
            </button>
            <button
              onClick={async () => {
                if (!cancellingChannel) return;

                setIsCancelling(true);
                try {
                  await api.delete(
                    `/v2/slack/journeys/${journeyId}/schedules/${cancellingChannel.id}?team=${teamId}`,
                  );

                  await refreshJourneyData();

                  // Note: Status cancellation is handled server-side
                  toast.success(
                    `Journey cancelled in ${cancellingChannel.name}`,
                  );

                  setIsCancelModalOpen(false);
                  setCancellingChannel(null);
                } catch (error) {
                  console.error("Error cancelling journey:", error);
                  toast.error("Failed to cancel journey");
                } finally {
                  setIsCancelling(false);
                }
              }}
              disabled={isCancelling}
              className="px-4 py-2 rounded-md bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-red-300"
            >
              {isCancelling ? "Cancelling..." : "Cancel Journey"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
